import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

export default function ProfilePage() {
  const { profile, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '??';

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSaved(false);

    if (!fullName.trim()) {
      setErrorMsg('Nome não pode ficar vazio');
      return;
    }

    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ full_name: fullName.trim() })
      .eq('id', profile?.id);

    setSaving(false);
    if (error) {
      setErrorMsg(error.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSaved(false);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMsg('Preencha todos os campos de senha');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('As senhas novas não conferem');
      return;
    }
    if (newPassword.length < 6) {
      setErrorMsg('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setSaving(false);
    if (error) {
      setErrorMsg(error.message === 'New password should be different from the old password.'
        ? 'A nova senha deve ser diferente da atual. Se você esqueceu a senha atual, use a opção "Esqueci minha senha" na tela de login.'
        : error.message);
    } else {
      setSaved(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const createdDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    : '—';

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Link to="/dashboard" className="text-sm text-foreground-500 hover:text-foreground-700 flex items-center gap-1">
        <i className="ri-arrow-left-line" /> Dashboard
      </Link>

      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950">Meu Perfil</h2>
        <p className="text-sm text-foreground-500 mt-1">Gerencie suas informações pessoais e segurança da conta</p>
      </div>

      {/* Status feedback */}
      {saved && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-emerald-800 animate-fadeIn">
          <i className="ri-check-line text-emerald-600" />
          Alterações salvas com sucesso!
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-red-800 animate-fadeIn">
          <i className="ri-error-warning-line text-red-600" />
          {errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left sidebar - avatar card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-background-200 p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-primary-700 text-2xl font-heading font-bold">{initials}</span>
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-950">{profile?.full_name}</h3>
            <p className="text-sm text-foreground-500 capitalize">{profile?.role || 'admin'}</p>
            <p className="text-xs text-foreground-400 mt-1">{user?.email}</p>

            <div className="mt-5 pt-5 border-t border-background-100 space-y-2 text-left">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">Membro desde</span>
                <span className="text-foreground-900 font-medium">{createdDate}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">Verificação email</span>
                {user?.email_confirmed_at ? (
                  <span className="flex items-center gap-1 text-emerald-600 font-medium text-xs">
                    <i className="ri-shield-check-line" /> Verificado
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-600 font-medium text-xs">
                    <i className="ri-shield-line" /> Pendente
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">ID da empresa</span>
                <span className="text-foreground-900 font-mono text-xs">{profile?.company_id?.slice(0, 8) || '—'}...</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right - forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile form */}
          <div className="bg-white rounded-xl border border-background-200 p-6">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-5">Informações Pessoais</h3>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nome completo</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full border border-background-200 rounded-lg px-3 py-2.5 text-sm text-foreground-900 bg-background-50 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="Seu nome completo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full border border-background-200 rounded-lg px-3 py-2.5 text-sm text-foreground-500 bg-background-100 cursor-not-allowed"
                />
                <p className="text-xs text-foreground-400 mt-1">Email vinculado à sua conta. Não pode ser alterado aqui.</p>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {saving ? (
                    <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin" /> Salvando...</span>
                  ) : 'Salvar alterações'}
                </button>
              </div>
            </form>
          </div>

          {/* Password form */}
          <div className="bg-white rounded-xl border border-background-200 p-6">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-5">Alterar Senha</h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Senha atual</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-background-200 rounded-lg px-3 py-2.5 text-sm text-foreground-900 bg-background-50 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="••••••••"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nova senha</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full border border-background-200 rounded-lg px-3 py-2.5 text-sm text-foreground-900 bg-background-50 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Confirmar nova senha</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full border border-background-200 rounded-lg px-3 py-2.5 text-sm text-foreground-900 bg-background-50 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="Repita a nova senha"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 bg-foreground-900 hover:bg-foreground-800 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {saving ? (
                    <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin" /> Alterando...</span>
                  ) : 'Alterar senha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}